import { cn } from "@/lib/utils";

interface InteractiveCalloutProps {
	label: string;
	/**
	 * "right": text sits up-left of the mockup, arrow curves right into it
	 * (desktop). "down": text sits above the mockup, arrow curves downward into
	 * it (stacked / mobile).
	 */
	direction?: "right" | "down";
	className?: string;
}

// Handwritten stack: refined script for Latin, app font (Cairo) picks up any
// Arabic glyphs Caveat doesn't cover, then a generic cursive fallback.
const HAND_FONT = "var(--font-hand), var(--font-app), cursive";

/**
 * A small hand-drawn designer annotation — casual handwritten label plus a
 * thin, organic arrow (with one natural loop and a few emphasis ticks) — that
 * points at the storefront mockup so it reads as interactive rather than a
 * static screenshot. Decorative only; hidden from assistive tech. In RTL the
 * arrow mirrors so it still points at the mockup while the text stays readable.
 */
function InteractiveCallout(prop: InteractiveCalloutProps) {
	const { label, direction = "right", className } = prop;
	const [lineOne, lineTwo] = splitLabel(label);

	return (
		<div
			aria-hidden
			className={cn(
				"pointer-events-none absolute select-none text-(--brand-1)",
				direction === "right" ? "text-start" : "text-center",
				className,
			)}
		>
			<p
				className={cn(
					"font-semibold leading-[1.05] whitespace-nowrap",
					direction === "right" ? "text-2xl" : "text-xl",
				)}
				style={{ fontFamily: HAND_FONT }}
			>
				{lineOne}
				<br />
				{lineTwo}
			</p>

			<CalloutArrow
				className={cn(
					"absolute",
					direction === "right"
						? "-inset-e-12 top-[74%] w-24 rtl:-scale-x-100"
						: "inset-s-1/2 top-full mt-8 w-23 -translate-x-1/2 rotate-58 rtl:translate-x-1/2 rtl:-scale-x-100 rtl:rotate-[-58deg]",
				)}
			/>
		</div>
	);
}

/**
 * Thin organic arrow: gentle curve → one small loop → short tip, with three
 * tiny emphasis ticks just past the arrowhead.
 */
function CalloutArrow(prop: { className?: string }) {
	return (
		<svg
			className={cn("overflow-visible", prop.className)}
			viewBox="0 0 134 72"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* main stroke with a natural mid loop */}
			<path d="M6 30C26 13 50 13 48 29C47 39 33 41 35 27C37 15 62 22 86 41C97 50 105 54 114 58" />
			{/* small arrowhead */}
			<path d="M114 58L100 57M114 58L109 44" />
			{/* emphasis ticks near the tip */}
			<path d="M121 44l6-3" />
			<path d="M124 53l7-1" />
			<path d="M120 62l6 3" />
		</svg>
	);
}

/**
 * Splits the label into two visual lines, matching the reference's stacked
 * layout. Breaks before the last word so the shorter word sits on line two.
 */
function splitLabel(label: string): [string, string] {
	const words = label.trim().split(/\s+/);
	if (words.length < 2) return [label, ""];
	const last = words.pop() as string;
	return [words.join(" "), last];
}

export default InteractiveCallout;
